// src/components/rooms/RoomForm.jsx
import React, { useState, useEffect } from 'react';
import { FiPlus, FiEdit2, FiX, FiMapPin, FiUsers, FiFileText, FiTool } from 'react-icons/fi';

const ROOM_TYPES = [
  { value: 'treatment', label: 'Treatment Room', icon: FiMapPin },
  { value: 'consultation', label: 'Consultation Room', icon: FiUsers },
  { value: 'group', label: 'Group Exercise Room', icon: FiUsers },
  { value: 'massage', label: 'Massage Room', icon: FiMapPin },
  { value: 'other', label: 'Other', icon: FiMapPin }
];

const COMMON_EQUIPMENT = [
  'Treatment Table', 'Chair', 'Storage Cabinet', 'Exercise Equipment', 
  'Mirror', 'Desk', 'Computer', 'Yoga Mats', 'Exercise Balls', 
  'Resistance Bands', 'Sound System', 'Massage Table', 'Towel Warmer', 
  'Essential Oils Storage'
];

export default function RoomForm({ initialData = null, onSave, onCancel, loading }) {
  const [formData, setFormData] = useState({
    name: '',
    capacity: 1,
    type: 'treatment',
    status: 'available',
    description: '',
    equipment: []
  });
  const [newEquipment, setNewEquipment] = useState('');

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        capacity: initialData.capacity || 1,
        type: initialData.type || 'treatment',
        status: initialData.status || 'available',
        description: initialData.description || '',
        equipment: Array.isArray(initialData.equipment) ? initialData.equipment : []
      });
    } else {
      setFormData({
        name: '',
        capacity: 1,
        type: 'treatment',
        status: 'available',
        description: '',
        equipment: []
      });
    }
  }, [initialData]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const room = {
      ...formData,
      id: initialData?.id,
      name: formData.name.trim(),
      capacity: Number(formData.capacity),
      createdAt: initialData?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    onSave(room);
    
    if (!initialData) {
      setFormData({
        name: '',
        capacity: 1,
        type: 'treatment',
        status: 'available',
        description: '',
        equipment: []
      });
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addEquipment = (equipment) => {
    if (equipment && !formData.equipment.includes(equipment)) {
      setFormData(prev => ({
        ...prev,
        equipment: [...prev.equipment, equipment]
      }));
    }
  };

  const removeEquipment = (equipment) => {
    setFormData(prev => ({
      ...prev,
      equipment: prev.equipment.filter(item => item !== equipment)
    }));
  };

  const addCustomEquipment = () => {
    if (newEquipment.trim()) {
      addEquipment(newEquipment.trim());
      setNewEquipment('');
    }
  };

  return (
    <div className="card-modern">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          {initialData ? <FiEdit2 className="h-5 w-5 text-blue-600" /> : <FiPlus className="h-5 w-5 text-green-600" />}
          <h3 className="text-lg font-medium text-gray-900">
            {initialData ? 'Edit Room' : 'Add New Room'}
          </h3>
        </div>
        <p className="text-sm text-gray-500 mt-1">
          {initialData ? 'Update room details and configuration' : 'Configure a new room for your clinic'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Room Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Treatment Room 1"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Capacity *
            </label>
            <input
              type="number"
              min="1"
              max="50"
              value={formData.capacity}
              onChange={(e) => handleInputChange('capacity', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              required
            />
          </div>
        </div>

        {/* Room Type and Status */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Room Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => handleInputChange('type', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              {ROOM_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleInputChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="available">Available</option>
              <option value="occupied">Occupied</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiFileText className="inline h-4 w-4 mr-1" />
            Description
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
            placeholder="Brief description of the room and its purpose..."
          />
        </div>

        {/* Equipment */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <FiTool className="inline h-4 w-4 mr-1" />
            Equipment
          </label>
          
          {/* Current Equipment */}
          {Array.isArray(formData.equipment) && formData.equipment.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.equipment.map((item, index) => (
                <span key={index} className="inline-flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-800 rounded-full">
                  {item}
                  <button
                    type="button"
                    onClick={() => removeEquipment(item)}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                  >
                    <FiX className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Common Equipment Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
            {COMMON_EQUIPMENT.filter(item => !Array.isArray(formData.equipment) || !formData.equipment.includes(item)).map(item => (
              <button
                key={item}
                type="button"
                onClick={() => addEquipment(item)}
                className="text-left px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50 hover:border-gray-300 transition-colors"
              >
                {item}
              </button>
            ))}
          </div>

          {/* Custom Equipment Input */}
          <div className="flex space-x-2">
            <input
              type="text"
              value={newEquipment}
              onChange={(e) => setNewEquipment(e.target.value)}
              placeholder="Add custom equipment..."
              className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addCustomEquipment())}
            />
            <button
              type="button"
              onClick={addCustomEquipment}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
            >
              Add
            </button>
          </div>
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200">
          {initialData && (
            <button
              type="button"
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={loading || !formData.name.trim()}
            className={`px-6 py-2 text-sm font-medium text-white rounded-md focus:ring-2 focus:ring-offset-2 disabled:opacity-50 transition-colors ${
              initialData 
                ? 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500' 
                : 'bg-green-600 hover:bg-green-700 focus:ring-green-500'
            }`}
          >
            {loading ? 'Saving...' : initialData ? 'Update Room' : 'Add Room'}
          </button>
        </div>
      </form>
    </div>
  );
}
